/*!
  Pioneer WFRIA Flexgrid v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Flexgrid"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Flexgrid") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Flexgrid");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "className", "columnGap", "rowGap", "height", "width", "spacing", "direction", "alignHorizontal", "alignVertical"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React from "react";
import { isEmpty, safeMerge } from "../common/utilities";
import Box from "../private/Box/Box";
import { FlexgridContext } from "./FlexgridContext";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";

function Flexgrid(_ref) {
  let {
    children,
    className,
    columnGap,
    rowGap,
    height,
    width,
    spacing,
    direction,
    alignHorizontal,
    alignVertical
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  let cssRoot = {};
  const theme = useTheme();
  const spacingRatio = !isEmpty(theme) ? theme.spacing : 1;
  const realColumnGap = columnGap * spacingRatio;
  const realRowGap = rowGap * spacingRatio;
  cssRoot = safeMerge(cssRoot, {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: direction,
    flexWrap: "wrap",
    justifyContent: alignHorizontal,
    alignItems: alignVertical,
    height,
    width,

    /**
     * Trick to account for the extra space taken
     * by the columnGap and rowGap that will be applied
     * to all FlexgridItems (see context and paddings).
     */
    marginLeft: realColumnGap * -1,
    marginTop: realRowGap * -1
  });
  const cssBox = {
    display: "block",
    height: "auto"
  };
  const context = {
    columnGap: columnGap * spacingRatio,
    rowGap: rowGap * spacingRatio
  };
  return _jsx(Box, {
    spacing: spacing,
    theme: theme,
    className: className,
    css: cssBox,
    children: _jsx("div", _objectSpread(_objectSpread({
      css: cssRoot
    }, otherProps), {}, {
      children: _jsx(FlexgridContext.Provider, {
        value: context,
        children: children
      })
    }))
  });
}

Flexgrid.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Children of the Flexgrid (FlexgridItem(s) or any other nodes).
   */
  children: PropTypes.node.isRequired,

  /**
   * Equivalent to "justify-content" in flexbox, this prop defines
   * the alignment along the main axis (horizontal).
   */
  alignHorizontal: PropTypes.oneOf(["flex-start", "center", "flex-end", "space-between", "space-around", "space-evenly"]),

  /**
   * Equivalent to "align-items" in flexbox, this prop defines
   * the alignment along the cross axis (vertical).
   */
  alignVertical: PropTypes.oneOf(["flex-start", "center", "flex-end", "stretch", "baseline"]),

  /**
   * Width of the gutters between the columns.
   * See the “Customization -> Spacing” section for
   * more information about this unit.
   */
  columnGap: PropTypes.number,

  /**
   * Equivalent to "flex-direction" in flexbox, this prop
   * establishes the main-axis, thus defining the direction
   * flex items are placed in the flex container. Flexbox is
   * (aside from optional wrapping) a single-direction layout
   * concept. Think of flex items as primarily laying out
   * either in horizontal rows or vertical columns.
   */
  direction: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),

  /**
   * The height of the Flexgrid.
   * It follows the [CSS height property](https://developer.mozilla.org/en-US/docs/Web/CSS/height).
   */
  height: PropTypes.string,

  /**
   * Width of the gutters between the rows.
   * See the “Customization -> Spacing” section for
   * more information about this unit.
   */
  rowGap: PropTypes.number,

  /**
   * Custom spacing for the component. See the
   * "Customization -> Spacing" section.
   */
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.shape()]),

  /**
   * The width of the Flexgrid.
   * It follows the [CSS width property](https://developer.mozilla.org/en-US/docs/Web/CSS/width).
   */
  width: PropTypes.string
} : {};
Flexgrid.defaultProps = {
  alignHorizontal: "flex-start",
  alignVertical: "flex-start",
  columnGap: 1,
  direction: "row",
  rowGap: 0,
  spacing: {},
  height: "auto",
  width: "auto"
};
export default Flexgrid;